import React, { useState } from "react";
import { rvFields, RvDto } from "../../types/rv";
import { PatientDto } from "../../types/patient";
import { DoctorDto } from "../../types/doctor";
import { usePatients } from "../../hooks/usePatients";
import { useDoctors } from "../../hooks/useDoctor";
import { PatientSearchSection } from "./PatientSearchSection";
import { DoctorSearchSection } from "./DoctorSearchSection";
import { AddAppointmentFields } from "./AddAppointmentFields";

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<RvDto>) => void;
}

const normalizeText = (str: string) =>
  str
    ? str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
    : "";

const createInitialFormData = (): Partial<RvDto> => ({
  patientId: 0,
  doctorId: 0, // Θα το γεμίζουμε δυναμικά από την επιλογή
  rvReason: "",
  day: new Date().toISOString().split("T")[0],
  startTime: "09:00",
  endTime: "09:30",
  status: 1,
  active: true,
});

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  // State για Ασθενή
  const [patientLastName, setPatientLastName] = useState("");
  const [patientFirstName, setPatientFirstName] = useState("");
  const [patientAmka, setPatientAmka] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<PatientDto | null>(
    null,
  );
  const [isPatientDropdownOpen, setIsPatientDropdownOpen] = useState(false);

  // State για Γιατρό (από τα μέλη της ιατρικής ομάδας)
  const [doctorLastName, setDoctorLastName] = useState("");
  const [doctorFirstName, setDoctorFirstName] = useState("");
  const [doctorAmka, setDoctorAmka] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDto | null>(null);
  const [isDoctorDropdownOpen, setIsDoctorDropdownOpen] = useState(false);

  const [validationError, setValidationError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<RvDto>>(
    createInitialFormData(),
  );

  // API calls για ασθενείς
  const patientSearchParams = {
    ...(patientLastName.trim() && { lastName: patientLastName.trim() }),
    ...(patientFirstName.trim() && { firstName: patientFirstName.trim() }),
    ...(patientAmka.trim() && { amka: patientAmka.trim() }),
  };
  const { patients = [], isLoading: isPatientsLoading } =
    usePatients(patientSearchParams);

  // API calls για γιατρούς (χρησιμοποιούμε τη νέα `medicalTeamMembers` από το hook μας)
  const { medicalTeamMembers = [], isLoading: isDoctorsLoading } = useDoctors();

  // Φιλτράρισμα ασθενών τοπικά
  const filteredPatients = (patients || []).filter((p: PatientDto) => {
    const pLastName = normalizeText(String(p.lastName || ""));
    const pFirstName = normalizeText(String(p.firstName || ""));
    const pAmka = String(p.amka || "").trim();

    return (
      (!patientLastName.trim() ||
        pLastName.includes(normalizeText(patientLastName.trim()))) &&
      (!patientFirstName.trim() ||
        pFirstName.includes(normalizeText(patientFirstName.trim()))) &&
      (!patientAmka.trim() || pAmka.includes(patientAmka.trim()))
    );
  });

  // Φιλτράρισμα γιατρών ιατρικής ομάδας τοπικά
  const filteredDoctors = (medicalTeamMembers || []).filter((d: DoctorDto) => {
    const dLastName = normalizeText(String(d.lastName || ""));
    const dFirstName = normalizeText(String(d.firstName || ""));
    const dAmka = String(d.amka || "").trim();

    return (
      (!doctorLastName.trim() ||
        dLastName.includes(normalizeText(doctorLastName.trim()))) &&
      (!doctorFirstName.trim() ||
        dFirstName.includes(normalizeText(doctorFirstName.trim()))) &&
      (!doctorAmka.trim() || dAmka.includes(doctorAmka.trim()))
    );
  });

  const handleClose = () => {
    setPatientLastName("");
    setPatientFirstName("");
    setPatientAmka("");
    setSelectedPatient(null);
    setIsPatientDropdownOpen(false);

    setDoctorLastName("");
    setDoctorFirstName("");
    setDoctorAmka("");
    setSelectedDoctor(null);
    setIsDoctorDropdownOpen(false);

    setValidationError(null);
    setFormData(createInitialFormData());
    onClose();
  };

  if (!isOpen) return null;

  const handleFieldChange = (name: keyof RvDto, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectPatient = (patient: PatientDto) => {
    const pId = Number(patient.id ?? (patient as any).patientId);
    setSelectedPatient(patient);
    setPatientLastName(String(patient.lastName || ""));
    setPatientFirstName(String(patient.firstName || ""));
    setPatientAmka(String(patient.amka || ""));
    setValidationError(null);
    setFormData(
      (prev): Partial<RvDto> => ({
        ...prev,
        patientId: pId,
        patientLastName: String(patient.lastName || ""),
        patientFirstName: String(patient.firstName || ""),
      }),
    );
    setIsPatientDropdownOpen(false);
  };

  const handleSelectDoctor = (doctor: DoctorDto) => {
    const dId = Number(doctor.doctorId);
    setSelectedDoctor(doctor);
    setDoctorLastName(String(doctor.lastName || ""));
    setDoctorFirstName(String(doctor.firstName || ""));
    setDoctorAmka(String(doctor.amka || ""));
    setValidationError(null);
    setFormData(
      (prev): Partial<RvDto> => ({
        ...prev,
        doctorId: dId,
      }),
    );
    setIsDoctorDropdownOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalPatientId = selectedPatient?.id
      ? Number(selectedPatient.id)
      : formData.patientId;

    const finalDoctorId = selectedDoctor?.doctorId
      ? Number(selectedDoctor.doctorId)
      : formData.doctorId;

    console.log("Payload που στέλνεται στον server:", {
      ...formData,
      patientId: finalPatientId,
      doctorId: finalDoctorId,
    });
    if (!finalPatientId || finalPatientId === 0) {
      setValidationError("Παρακαλώ επιλέξτε έναν υπαρκτό ασθενή από τη λίστα.");
      return;
    }

    if (!finalDoctorId || finalDoctorId === 0) {
      setValidationError(
        "Παρακαλώ επιλέξτε έναν γιατρό από την ιατρική ομάδα.",
      );
      return;
    }

    onSubmit({
      ...formData,
      patientId: finalPatientId,
      doctorId: finalDoctorId,
    });
    handleClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto relative">
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h3 className="text-xl font-bold text-gray-800">Νέο Ραντεβού</h3>
          <button
            onClick={handleClose}
            type="button"
            className="text-gray-400 hover:text-gray-600 font-bold text-xl leading-none"
          >
            &times;
          </button>
        </div>

        {validationError && (
          <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
            {validationError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Section Αναζήτησης Ασθενή */}
          <PatientSearchSection
            lastName={patientLastName}
            setLastName={setPatientLastName}
            firstName={patientFirstName}
            setFirstName={setPatientFirstName}
            amka={patientAmka}
            setAmka={setPatientAmka}
            selectedPatient={selectedPatient}
            setSelectedPatient={setSelectedPatient}
            isDropdownOpen={isPatientDropdownOpen}
            setIsDropdownOpen={setIsPatientDropdownOpen}
            filteredPatients={filteredPatients}
            isPatientsLoading={isPatientsLoading}
            onSelectPatient={handleSelectPatient}
          />

          {/* Section Αναζήτησης Ιατρού (από τα μέλη) */}
          <DoctorSearchSection
            lastName={doctorLastName}
            setLastName={setDoctorLastName}
            firstName={doctorFirstName}
            setFirstName={setDoctorFirstName}
            amka={doctorAmka}
            setamka={setDoctorAmka}
            selectedDoctor={selectedDoctor}
            setSelectedDoctor={setSelectedDoctor}
            isDropdownOpen={isDoctorDropdownOpen}
            setIsDropdownOpen={setIsDoctorDropdownOpen}
            filteredDoctors={filteredDoctors}
            isDoctorsLoading={isDoctorsLoading}
            onSelectDoctor={handleSelectDoctor}
          />

          <AddAppointmentFields
            fields={rvFields}
            formData={formData}
            onChange={handleFieldChange}
          />

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white font-bold rounded-lg hover:bg-darkprimary text-sm shadow-sm"
            >
              Αποθήκευση Ραντεβού
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;
